import { useTranslation } from 'react-i18next';
import { srOnly, usePrefersReducedMotion } from '../organic/motion';

/*
 * The square a camera app draws where a tap asked it to focus (useTapFocus.js
 * gives `ring`): it closes in on the point, turns green once the camera has
 * focused and metered there, red when the camera refused, grey with a word
 * where the camera focuses by itself; then it fades. Over the live picture of
 * every in-app camera, never in the way of a tap (pointer-events: none).
 * Reduced motion: no closing in, only the fade. The status line says it for
 * a screen reader.
 */
const COLOUR = {
  pending: '#fff',
  ok: 'var(--c-success)',
  refused: 'var(--c-danger)',
  auto: 'rgba(255, 255, 255, 0.6)',
};
const CSS = `
@keyframes cam-focus-in { from { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
  30% { opacity: 1; } to { transform: translate(-50%, -50%) scale(1); opacity: 1; } }
@keyframes cam-focus-out { from { opacity: 1; } to { opacity: 0; } }
.cam-focus-ring { animation: cam-focus-in 220ms ease-out, cam-focus-out 300ms ease-in 900ms forwards; }
.cam-focus-ring.cam-focus-still { animation: cam-focus-out 300ms ease-in 900ms forwards; }`;

export default function FocusRing({ ring, testIdPrefix = 'camera', zIndex = 1 }) {
  const { t } = useTranslation('camera');
  const reduced = usePrefersReducedMotion();
  const said = !ring ? '' : ring.status === 'auto' ? t('focus.ringAuto') : t('focus.ringAt');
  return (
    <>
      {ring && (
        <>
          <style>{CSS}</style>
          <div key={ring.id} aria-hidden="true" data-testid={`${testIdPrefix}-focus-ring`} data-status={ring.status}
               className={reduced ? 'cam-focus-ring cam-focus-still' : 'cam-focus-ring'}
               style={{ ...ringStyle, zIndex, left: ring.left, top: ring.top, borderColor: COLOUR[ring.status] }}>
            {ring.status === 'auto' && <span style={labelStyle}>{t('focus.ringAuto')}</span>}
          </div>
        </>
      )}
      <span role="status" style={srOnly}>{said}</span>
    </>
  );
}

const ringStyle = {
  position: 'absolute', width: 72, height: 72, boxSizing: 'border-box', border: '2px solid', borderRadius: 6,
  transform: 'translate(-50%, -50%)', pointerEvents: 'none',
  // Seen on a white sky and on dark leaves alike.
  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(0, 0, 0, 0.3)',
  transition: 'border-color 150ms linear',
};
const labelStyle = {
  position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 6, whiteSpace: 'nowrap',
  padding: '2px 8px', borderRadius: 999, background: 'rgba(0, 0, 0, 0.7)', color: '#fff', fontSize: 12,
};
